import { useToast } from "@dgraph-io/typhoon-ui";
import { useSubscribePlan } from "gql/hooks/billing/billing.hook";
import { useGetCurrentCloudUser } from "gql/hooks/users/users.hook";
import { ToastContext } from "libs/toast";
import trackEvent, { events } from "libs/trackevents";
import { useCallback, useState } from "react";

export const useAddCreditCard = () => {
    const toast = useToast(ToastContext);
    const { refetchCloudUser } = useGetCurrentCloudUser();
    const [isCardModalOpen, setIsCardModalOpenState] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { subscribe } = useSubscribePlan();

    const setIsCardModalOpen = useCallback((isOpen: boolean) => {
        if (isOpen) {
            trackEvent(events.creditCardPromptShown);
        }
        setIsCardModalOpenState(isOpen);
    }, []);

    const onMoveToPaidPlan = useCallback(
        async (sourceID: string) => {
            setIsCardModalOpen(false);
            setIsLoading(true);
            const toastID = toast.addToast({
                description: "Updating payment details",
                variant: "info",
                permanent: true,
            });
            try {
                await subscribe(sourceID);
                await refetchCloudUser();
                toast.removeToast(toastID);
                toast.addToast({
                    description: "Added payment details successfully",
                    variant: "success",
                });
                trackEvent(events.creditCardAddSuccess);
                setIsLoading(false);
            } catch (error) {
                trackEvent(events.creditCardAddFail);
                toast.removeToast(toastID);
                toast.addToast({
                    variant: "error",
                    heading: "Unable to add payment details.",
                    description:
                        error?.message || "Oops. Something went wrong.",
                });
                setIsLoading(false);
                throw error;
            }
        },
        [refetchCloudUser, subscribe, toast, setIsCardModalOpen]
    );

    return {
        isLoading,
        onMoveToPaidPlan,
        isCardModalOpen,
        setIsCardModalOpen,
    };
};
